import React from "react";
import { motion } from "framer-motion";
import {
  Boxes, Sparkles, LayoutGrid, Gauge, Volume2, FlaskConical,
  ThermometerSnowflake, Truck,
} from "lucide-react";
import { IMG } from "@/lib/site";
import { MotionSection, MotionStagger, MotionItem } from "@/components/MotionSection";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const FEATURES = [
  {
    icon: Boxes,
    title: "Compact countertop footprint",
    body: "Slim, considered profile that sits cleanly on a worksurface or shelf.",
  },
  {
    icon: Sparkles,
    title: "Clean clinical appearance",
    body: "Minimal styling chosen to suit lab, clinic and home-lab environments.",
  },
  {
    icon: LayoutGrid,
    title: "Internal organisation tray",
    body: "Structured interior keeps vials upright, visible and accessible.",
  },
  {
    icon: Gauge,
    title: "Front-mounted digital LCD",
    body: "Live internal temperature readout with ON/OFF and ▲▼ controls.",
  },
  {
    icon: Volume2,
    title: "Quiet operation",
    body: "Engineered to run discreetly — suitable for shared workspaces.",
  },
  {
    icon: FlaskConical,
    title: "Research-use storage environments",
    body: "A dedicated, compact storage solution for temperature-sensitive materials.",
  },
];

const PILLARS = [
  {
    icon: ThermometerSnowflake,
    title: "Steady 2–8 °C",
    body: "Front-panel LCD shows live internal temperature.",
  },
  {
    icon: Volume2,
    title: "Whisper-quiet",
    body: "Under 28 dB — quiet enough for a desk or clinic room.",
  },
  {
    icon: Truck,
    title: "Designed in the UK · Ships July 2026",
    body: "UK tracked delivery once dispatched.",
  },
];

export default function Showcase() {
  const reduce = usePrefersReducedMotion();

  return (
    <MotionSection
      id="showcase"
      data-testid="section-showcase"
      className="bg-white py-20 lg:py-28"
    >
      <div className="container-pc">
        <div className="max-w-2xl">
          <p className="eyebrow">The PeptiChill Cooler Fridge</p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-navy tracking-tighter">
            Designed for clean, organised peptide storage.
          </h2>
          <p className="mt-6 text-base lg:text-[17px] text-muted leading-[1.75] max-w-xl">
            Each unit carries the Peptigen Labs identity on the front panel — a quiet signal
            of the sister-brand engineering standards behind it.
          </p>
        </div>

        {/* Imagery grid — open-lid hero of this section */}
        <div className="mt-12 grid lg:grid-cols-12 gap-5 lg:gap-6 items-stretch">
          <figure
            className="lg:col-span-7 rounded-2xl bg-ice-light overflow-hidden border border-silver/40 shadow-card"
            data-testid="showcase-open-lid"
          >
            <img
              src={IMG.openLid}
              alt="PeptiChill cooler with the top lid lifted open, showing internal organisation tray and front LCD at 2°C"
              className="block w-full max-w-full h-full object-cover blend-product"
              loading="lazy"
              width="900"
              height="800"
            />
          </figure>
          <div className="lg:col-span-5 grid grid-cols-1 gap-5 lg:gap-6">
            <figure className="rounded-2xl bg-ice-light overflow-hidden border border-silver/40 shadow-card">
              <img
                src={IMG.hero}
                alt="PeptiChill cooler front 3/4 view with Peptigen Labs logo on the front face"
                className="block w-full max-w-full h-full object-cover blend-product"
                loading="lazy"
                width="600"
                height="600"
              />
            </figure>
            <figure className="rounded-2xl bg-ice-light overflow-hidden border border-silver/40 shadow-card">
              <img
                src={IMG.lifestyle}
                alt="PeptiChill cooler on a white marble countertop alongside research vials"
                className="block w-full max-w-full h-full object-cover"
                loading="lazy"
                width="800"
                height="500"
              />
            </figure>
          </div>
        </div>

        {/* Lid-open feature callout */}
        <div
          className="mt-16 lg:mt-20 max-w-3xl"
          data-testid="showcase-lid-callout"
        >
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-navy tracking-tighter leading-tight">
            Lift the lid. Everything in its place.
          </h3>
          <p className="mt-4 text-base text-muted leading-relaxed">
            Internal organisation tray keeps vials upright, visible and accessible — designed
            for organised storage of research-use peptides and temperature-sensitive
            laboratory materials.
          </p>
        </div>

        {/* Trust pillars */}
        <MotionStagger
          className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6"
          data-testid="showcase-pillars"
        >
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <MotionItem
              key={title}
              as="article"
              data-testid={`showcase-pillar-${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
            >
              <motion.div
                whileHover={reduce ? undefined : { scale: 1.02, y: -2 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="rounded-2xl bg-white border border-ice p-5 shadow-card h-full"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-navy text-cyan">
                  <Icon size={18} strokeWidth={1.9} />
                </div>
                <h4 className="mt-4 text-base font-semibold text-navy tracking-tight">
                  {title}
                </h4>
                <p className="mt-1.5 text-sm text-muted leading-relaxed">{body}</p>
              </motion.div>
            </MotionItem>
          ))}
        </MotionStagger>

        {/* Original 6 feature cards */}
        <MotionStagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <MotionItem
              key={title}
              as="article"
              data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <motion.div
                whileHover={reduce ? undefined : { scale: 1.02, y: -2 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="rounded-2xl bg-white border border-ice p-6 shadow-card h-full"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-navy text-cyan border border-navy">
                  <Icon size={20} strokeWidth={1.8} />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-navy tracking-tight">
                  {title}
                </h3>
                <p className="mt-2 text-sm text-muted leading-relaxed">{body}</p>
              </motion.div>
            </MotionItem>
          ))}
        </MotionStagger>
      </div>
    </MotionSection>
  );
}

function LcdMacroCard({ reduce }) {
  return (
    <motion.div
      data-testid="showcase-lcd-macro-card"
      className="mt-12 lg:mt-16 rounded-3xl bg-gradient-to-br from-ice-light to-white border border-silver/40 shadow-card overflow-hidden"
      initial={reduce ? false : { opacity: 0, y: 16 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="grid lg:grid-cols-2 items-center gap-0">
        <div className="relative bg-ice-light/60 overflow-hidden">
          <motion.img
            src={IMG.lcdMacro}
            alt="Macro close-up of the front-mounted LCD showing 2°C with ON/OFF and ▲▼ controls"
            className="block w-full h-auto object-contain"
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            whileInView={reduce ? undefined : { opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            animate={reduce ? undefined : {
              filter: [
                "drop-shadow(0 0 0px rgba(79,195,217,0.0))",
                "drop-shadow(0 0 14px rgba(79,195,217,0.55))",
                "drop-shadow(0 0 0px rgba(79,195,217,0.0))",
              ],
              transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.8 },
            }}
            loading="lazy"
            width="900"
            height="700"
          />
        </div>
        <div className="p-8 lg:p-12">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-navy/80">
            Precision Readout
          </p>
          <h3 className="mt-4 text-2xl sm:text-3xl lg:text-[34px] font-bold text-navy tracking-tighter leading-tight">
            Live internal temperature on the front panel.
          </h3>
          <p className="mt-4 text-base text-muted leading-relaxed">
            A clear digital display keeps the operating temperature visible at a
            glance — set, monitor, and confirm without opening the lid.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
